import React from 'react';
import { useTranslation } from 'react-i18next';
import { useProgrammerContext } from '@/hid/ProgrammerContext';
import { isWebHIDSupported, getWebHIDUnsupportedReason } from '@/hid/compatibility';

const ProgrammerSelector: React.FC = () => {
  const { t } = useTranslation('webflash');
  const {
    programmerInfo,
    status,
    error,
    connectNuLink,
    connectSnLink,
    disconnect,
  } = useProgrammerContext();

  const webHIDSupported = isWebHIDSupported();
  const unsupportedReason = getWebHIDUnsupportedReason();
  const isConnected = status === 'connected';
  const isBusy = status === 'busy';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t('programmer.title')}
      </h3>

      {!webHIDSupported ? (
        <p className="text-sm text-red-500">{unsupportedReason}</p>
      ) : (
        <div className="space-y-4">
          {/* Status indicator */}
          <div className="flex items-center gap-3">
            <div
              className={`w-3 h-3 rounded-full ${
                isConnected
                  ? 'bg-green-500'
                  : status === 'error'
                    ? 'bg-red-500'
                    : isBusy
                      ? 'bg-yellow-500 animate-pulse'
                      : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {isConnected
                ? t('programmer.connected')
                : isBusy
                  ? t('programmer.connecting')
                  : t('programmer.disconnected')}
            </span>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          {/* Programmer info */}
          {programmerInfo && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('programmer.name')}</span>
              <span className="font-mono text-gray-900 dark:text-white">{programmerInfo.name}</span>
              <span className="text-gray-500 dark:text-gray-400">{t('programmer.firmware')}</span>
              <span className="font-mono text-gray-900 dark:text-white">
                v{programmerInfo.firmwareVersion}
              </span>
              <span className="text-gray-500 dark:text-gray-400">VID:PID</span>
              <span className="font-mono text-gray-900 dark:text-white">
                {`0x${programmerInfo.vid.toString(16).toUpperCase().padStart(4, '0')}:0x${programmerInfo.pid.toString(16).toUpperCase().padStart(4, '0')}`}
              </span>
            </div>
          )}

          {/* Connect/disconnect buttons */}
          {isConnected ? (
            <button
              onClick={disconnect}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 transition-colors"
            >
              {t('programmer.disconnect')}
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={connectNuLink}
                disabled={isBusy}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {t('programmer.connectNuLink')}
              </button>
              <button
                onClick={connectSnLink}
                disabled={isBusy}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {t('programmer.connectSnLink')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgrammerSelector;
